import { Photo, Trash, XMark } from "@medusajs/icons"
import { Button, Checkbox, Drawer, IconButton, Input, Prompt, Text, Textarea, toast } from "@medusajs/ui"
import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useBlocker, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { CollectionType, AutomatedMembers } from "@/components/collection-rules"
import { Field } from "@/components/form"
import { FilePicker } from "@/components/file-picker"
import { DataTable, EmptyState, ErrorState, PageHeader, SaveBar, SearchInput, Section, Status, Thumb, usePage } from "@/components/ui"
import { slugify, uploadFiles } from "@/lib/catalog"
import {
  emptyCollection,
  useCollection,
  useCollectionList,
  useCollectionMembership,
  useCollectionProducts,
  useAllCollectionProducts,
  useDeleteCollection,
  useSaveCollection,
  SORT_ORDERS,
  type CollectionDraft,
  type SortOrder,
} from "@/lib/collections"
import { Badge, FilterSelect } from "@/terabizz"
import { useProducts } from "@/lib/api"
import { formatDate } from "@/lib/format"
import { useCan } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"

const PAGE = 25

export function CollectionsPage() {
  const [params, setParams] = useSearchParams()
  const { pageIndex, q, setPage, setQ } = usePage(params, setParams)
  const { data, isLoading, error } = useCollectionList({ q, limit: PAGE, offset: pageIndex * PAGE })
  const navigate = useNavigate()
  const can = useCan()

  return (
    <>
      <PageHeader
        title="Collections"
        actions={
          can("products:write") && (
            <Button size="small" asChild>
              <Link to="/collections/new">Create collection</Link>
            </Button>
          )
        }
      />
      <Section>
        <div className="px-4 py-3">
          <SearchInput value={q} onChange={setQ} placeholder="Search collections" />
        </div>
        {error ? (
          <ErrorState error={error} />
        ) : (
          <DataTable
            rowKey={(c: any) => c.id}
            rows={data?.collections}
            count={data?.count ?? 0}
            pageSize={PAGE}
            pageIndex={pageIndex}
            onPageChange={setPage}
            loading={isLoading}
            onRowClick={(c: any) => navigate(`/collections/${c.id}`)}
            mobile={(c: any) => (
              <div className="flex items-center gap-x-3">
                <Thumb src={c.metadata?.image} />
                <div className="min-w-0">
                  <div className="text-[13px] font-medium">{c.title}</div>
                  <div className="text-[12px] text-ui-fg-subtle">{c.product_count ?? 0} products</div>
                </div>
              </div>
            )}
            empty={<EmptyState title="No collections yet" body="Create a collection to group products on your store." />}
            columns={[
              {
                key: "title",
                header: "Title",
                cell: (c: any) => (
                  <div className="flex items-center gap-x-3">
                    <Thumb src={c.metadata?.image} size={36} />
                    <span className="txt-compact-small-plus">{c.title}</span>
                  </div>
                ),
              },
              { key: "products", header: "Products", cell: (c: any) => <span className="inline-flex items-center gap-x-2">{c.product_count ?? "—"}{c.metadata?.terify_rules ? <Badge tone="info">Automated</Badge> : null}</span> },
              { key: "conditions", header: "Product conditions", cell: (c: any) => { const n = c.metadata?.terify_rules?.rules?.length; return <span className="text-ui-fg-subtle">{c.metadata?.terify_rules ? `${n ?? 0} condition${n === 1 ? "" : "s"}` : "Manual"}</span> } },
              { key: "updated", header: "Updated", cell: (c: any) => formatDate(c.updated_at, false) },
            ]}
          />
        )}
      </Section>
    </>
  )
}

export function CollectionEditorPage() {
  const { id: routeId } = useParams()
  const id = !routeId || routeId === "new" ? null : routeId
  const { data, error, isLoading } = useCollection(id)
  if (error) return <ErrorState error={error} />
  if (id && (isLoading || !data)) return null
  return <CollectionEditor key={id ?? "new"} id={id} initial={data?.draft ?? null} />
}

function CollectionEditor({ id, initial }: { id: string | null; initial: CollectionDraft | null }) {
  const navigate = useNavigate()
  const canWrite = useCan()("products:write")
  const base = useMemo(() => initial ?? emptyCollection(), [initial])
  const [d, setD] = useState(base)
  const [handleTouched, setHandleTouched] = useState(!!initial)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [pickingImage, setPickingImage] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const save = useSaveCollection()
  const del = useDeleteCollection()
  const dirty = JSON.stringify(d) !== JSON.stringify(base)
  useEffect(() => setD(base), [base])
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && !save.isPending && currentLocation.pathname !== nextLocation.pathname)
  const set = <K extends keyof CollectionDraft>(k: K, v: CollectionDraft[K]) => setD((x) => ({ ...x, [k]: v }))

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!d.title.trim()) return toast.error("Title can't be blank.")
    save.mutate(
      { id, draft: d },
      {
        onSuccess: (c: any) => {
          toast.success(id ? "Collection saved" : "Collection created")
          if (!id) navigate(`/collections/${c.id}`, { replace: true })
        },
        onError: (err) => toast.error(errorMessage(err)),
      }
    )
  }

  return (
    <form onSubmit={onSubmit} className="pb-24">
      <PageHeader
        back="/collections"
        title={id ? base.title : "Create collection"}
        actions={
          id &&
          canWrite && (
            <Button type="button" size="small" variant="secondary" onClick={() => setConfirmDelete(true)}>
              <Trash /> Delete
            </Button>
          )
        }
      />
      <fieldset disabled={!canWrite} className="grid gap-4 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
          <Section>
            <div className="flex flex-col gap-y-4 px-4 py-4">
              <Field label="Title">
                <Input
                  value={d.title}
                  placeholder="e.g. Summer collection, Under $100, Staff picks"
                  onChange={(e) => {
                    const title = e.target.value
                    setD((x) => ({ ...x, title, handle: handleTouched ? x.handle : slugify(title) }))
                  }}
                />
              </Field>
              <Field label="Description" hint="Shown at the top of the collection page.">
                <Textarea rows={5} value={d.description} onChange={(e) => set("description", e.target.value)} />
              </Field>
            </div>
          </Section>

          <CollectionType value={d.rules} onChange={(rules) => set("rules", rules)} disabled={!canWrite} />

          {id && (
            <Section>
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div>
                  <Text size="small" weight="plus">Product order</Text>
                  <Text size="xsmall" className="text-ui-fg-subtle">How products are sorted on this collection's page. Shoppers can still choose another sort.</Text>
                </div>
                <FilterSelect
                  label="Sort"
                  allowAll={false}
                  value={d.sort_order}
                  onChange={(v) => set("sort_order", v as SortOrder)}
                  options={SORT_ORDERS.filter(([k]) => k !== "manual" || !d.rules)}
                />
              </div>
            </Section>
          )}
          {id && !d.rules && d.sort_order === "manual" ? (
            <ManualOrder id={id} order={d.product_order} onChange={(o) => set("product_order", o)} canWrite={canWrite} />
          ) : id && d.rules && base.rules ? <AutomatedMembers id={id} /> : id && !d.rules ? <CollectionProducts id={id} canWrite={canWrite} /> : id ? (
            <Section title="Products"><EmptyState title="Save to apply the conditions" body="The matching products will be listed here." /></Section>
          ) : (
            <Section title="Products">
              <EmptyState title="Save the collection first" body={d.rules ? "Matching products are listed here once it's saved." : "Then add products to it here."} />
            </Section>
          )}

          <Section title="Search engine listing">
            <div className="flex flex-col gap-y-4 px-4 py-4">
              <div className="rounded-md border bg-ui-bg-subtle p-3">
                <Text size="small" className="text-ui-fg-interactive">{d.seo_title || d.title || "Collection title"}</Text>
                <Text size="xsmall" className="text-ui-fg-muted">/collections/{d.handle || slugify(d.title) || "handle"}</Text>
                <Text size="xsmall" className="mt-1 text-ui-fg-subtle">{d.seo_description || d.description.slice(0, 160) || "Add a description to see how this collection might appear in search results."}</Text>
              </div>
              <Field label="Page title" hint={`${d.seo_title.length} of 70 characters used`}>
                <Input value={d.seo_title} onChange={(e) => set("seo_title", e.target.value)} />
              </Field>
              <Field label="Meta description" hint={`${d.seo_description.length} of 160 characters used`}>
                <Textarea rows={3} value={d.seo_description} onChange={(e) => set("seo_description", e.target.value)} />
              </Field>
              <Field label="URL handle">
                <Input
                  value={d.handle}
                  onChange={(e) => {
                    setHandleTouched(true)
                    set("handle", slugify(e.target.value) || e.target.value.toLowerCase())
                  }}
                />
              </Field>
            </div>
          </Section>
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <Section title="Image">
            <div className="p-6">
              {d.image ? (
                <div className="group relative overflow-hidden rounded-md border">
                  <img src={d.image} alt="" className="aspect-[4/3] w-full object-cover" />
                  <IconButton type="button" size="2xsmall" className="absolute right-1 top-1" aria-label="Remove image" onClick={() => set("image", "")}>
                    <XMark />
                  </IconButton>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed text-ui-fg-muted hover:bg-ui-bg-base-hover"
                >
                  <Photo />
                  <Text size="xsmall">{uploading ? "Uploading…" : "Add image"}</Text>
                </button>
              )}
              <Button type="button" size="small" variant="secondary" className="mt-2" onClick={() => setPickingImage(true)}>Select existing</Button>
              {pickingImage && <FilePicker title="Select image" onClose={() => setPickingImage(false)} onDone={([f]) => { setPickingImage(false); set("image", f.url) }} />}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={async (e) => {
                  const f = e.target.files?.[0]
                  e.target.value = ""
                  if (!f) return
                  setUploading(true)
                  try {
                    const [url] = await uploadFiles([f])
                    set("image", url)
                  } catch (err) {
                    toast.error(errorMessage(err))
                  } finally {
                    setUploading(false)
                  }
                }}
              />
            </div>
          </Section>
        </div>
      </fieldset>

      {canWrite && <SaveBar visible={dirty || !id} message={id ? "Unsaved collection changes" : "Unsaved collection"} saving={save.isPending} onDiscard={() => (id ? setD(base) : navigate("/collections"))} />}

      <Prompt open={blocker.state === "blocked"}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Leave page with unsaved changes?</Prompt.Title>
            <Prompt.Description>Leaving this page will discard all unsaved changes.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel onClick={() => blocker.reset?.()}>Stay</Prompt.Cancel>
            <Prompt.Action onClick={() => blocker.proceed?.()}>Leave page</Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
      <Prompt open={confirmDelete} onOpenChange={setConfirmDelete}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Delete {base.title}?</Prompt.Title>
            <Prompt.Description>The products stay in your store; they just leave this collection.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Cancel</Prompt.Cancel>
            <Prompt.Action
              onClick={() =>
                id &&
                del.mutate(id, {
                  onSuccess: () => {
                    toast.success("Collection deleted")
                    setD(base)
                    navigate("/collections", { replace: true })
                  },
                  onError: (err) => toast.error(errorMessage(err)),
                })
              }
            >
              Delete collection
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </form>
  )
}

/**
 * Manual sort: every product in the collection as a list you drag (or move with
 * the arrow buttons) into the order shoppers see. Saved with the collection.
 */
function ManualOrder({ id, order, onChange, canWrite }: { id: string; order: string[]; onChange: (o: string[]) => void; canWrite: boolean }) {
  const { data: products, isLoading } = useAllCollectionProducts(id, true)
  const members = useCollectionMembership(id)
  const [browse, setBrowse] = useState(false)
  const [dragging, setDragging] = useState<string | null>(null)
  const list = useMemo(() => {
    const all = products ?? []
    const pos = new Map(order.map((pid, i) => [pid, i]))
    return [...all].sort((a, b) => (pos.get(a.id) ?? 1e6) - (pos.get(b.id) ?? 1e6) || String(a.title).localeCompare(String(b.title)))
  }, [products, order])
  const move = (from: number, to: number) => {
    if (to < 0 || to >= list.length || from === to) return
    const ids = list.map((p) => p.id)
    const [x] = ids.splice(from, 1)
    ids.splice(to, 0, x)
    onChange(ids)
  }
  return (
    <Section
      title="Products"
      actions={canWrite && <Button type="button" size="small" variant="secondary" onClick={() => setBrowse(true)}>Browse</Button>}
    >
      {isLoading ? (
        <div className="h-24 animate-pulse" />
      ) : !list.length ? (
        <EmptyState title="No products in this collection" body={canWrite ? "Use Browse to add products." : undefined} />
      ) : (
        <ol className="divide-y divide-[var(--tb-line)]" aria-label="Products in display order">
          {list.map((p, i) => (
            <li
              key={p.id}
              draggable={canWrite}
              onDragStart={() => setDragging(p.id)}
              onDragEnd={() => setDragging(null)}
              onDragOver={(e) => {
                e.preventDefault()
                if (!dragging || dragging === p.id) return
                move(list.findIndex((x) => x.id === dragging), i)
              }}
              className={`flex items-center gap-x-3 px-4 py-2 ${dragging === p.id ? "bg-ui-bg-subtle opacity-60" : ""}`}
            >
              {canWrite && (
                <span className="cursor-grab text-ui-fg-muted" aria-hidden="true">
                  <svg viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor"><circle cx="7.5" cy="5" r="1.3" /><circle cx="12.5" cy="5" r="1.3" /><circle cx="7.5" cy="10" r="1.3" /><circle cx="12.5" cy="10" r="1.3" /><circle cx="7.5" cy="15" r="1.3" /><circle cx="12.5" cy="15" r="1.3" /></svg>
                </span>
              )}
              <span className="w-6 text-right text-[12px] tabular-nums text-ui-fg-muted">{i + 1}.</span>
              <Thumb src={p.thumbnail} size={32} />
              <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{p.title}</span>
              <Status value={p.metadata?.archived ? "archived" : p.status} />
              {canWrite && (
                <span className="flex items-center">
                  <IconButton type="button" size="small" variant="transparent" aria-label={`Move ${p.title} up`} disabled={i === 0} onClick={() => move(i, i - 1)}>
                    <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5 10 7.5l5 5" /></svg>
                  </IconButton>
                  <IconButton type="button" size="small" variant="transparent" aria-label={`Move ${p.title} down`} disabled={i === list.length - 1} onClick={() => move(i, i + 1)}>
                    <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 7.5l5 5 5-5" /></svg>
                  </IconButton>
                  <IconButton
                    type="button"
                    size="small"
                    variant="transparent"
                    aria-label={`Remove ${p.title} from collection`}
                    onClick={() => members.mutate({ remove: [p.id] }, { onSuccess: () => toast.success("Removed from collection"), onError: (err) => toast.error(errorMessage(err)) })}
                  >
                    <XMark />
                  </IconButton>
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
      {browse && <BrowseProducts collectionId={id} onClose={() => setBrowse(false)} onAdd={(ids) => members.mutateAsync({ add: ids })} />}
    </Section>
  )
}

function CollectionProducts({ id, canWrite }: { id: string; canWrite: boolean }) {
  const [q, setQ] = useState("")
  const [page, setPage] = useState(0)
  const { data, isLoading } = useCollectionProducts(id, { q, limit: 10, offset: page * 10 })
  const members = useCollectionMembership(id)
  const [browse, setBrowse] = useState(false)
  const navigate = useNavigate()

  return (
    <Section
      title="Products"
      actions={
        canWrite && (
          <Button type="button" size="small" variant="secondary" onClick={() => setBrowse(true)}>
            Browse
          </Button>
        )
      }
    >
      <div className="px-4 py-3">
        <SearchInput value={q} onChange={(v) => { setQ(v); setPage(0) }} placeholder="Search products in this collection" />
      </div>
      <DataTable
        rowKey={(p: any) => p.id}
        rows={data?.products}
        count={data?.count ?? 0}
        pageSize={10}
        pageIndex={page}
        onPageChange={setPage}
        loading={isLoading}
        onRowClick={(p: any) => navigate(`/products/${p.id}`)}
        empty={<EmptyState title="No products in this collection" body={canWrite ? "Use Browse to add products." : undefined} />}
        columns={[
          {
            key: "title",
            header: "Product",
            cell: (p: any) => (
              <div className="flex items-center gap-x-3">
                <Thumb src={p.thumbnail} size={36} />
                <span className="txt-compact-small-plus">{p.title}</span>
              </div>
            ),
          },
          { key: "status", header: "Status", cell: (p: any) => <Status value={p.metadata?.archived ? "archived" : p.status} /> },
          {
            key: "remove",
            header: "",
            className: "w-10",
            cell: (p: any) =>
              canWrite && (
                <IconButton
                  type="button"
                  size="small"
                  variant="transparent"
                  aria-label="Remove from collection"
                  onClick={(e) => {
                    e.stopPropagation()
                    members.mutate({ remove: [p.id] }, { onSuccess: () => toast.success("Removed from collection"), onError: (err) => toast.error(errorMessage(err)) })
                  }}
                >
                  <XMark />
                </IconButton>
              ),
          },
        ]}
      />
      {browse && <BrowseProducts collectionId={id} onClose={() => setBrowse(false)} onAdd={(ids) => members.mutateAsync({ add: ids })} />}
    </Section>
  )
}

/** Shopify's "Browse products" picker: search, tick, add. */
function BrowseProducts({ collectionId, onClose, onAdd }: { collectionId: string; onClose: () => void; onAdd: (ids: string[]) => Promise<unknown> }) {
  const [q, setQ] = useState("")
  const { data, isLoading } = useProducts({ q, limit: 50, offset: 0 })
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>Add products</Drawer.Title>
        </Drawer.Header>
        <Drawer.Body className="flex flex-col gap-y-3 overflow-y-auto">
          <SearchInput value={q} onChange={setQ} placeholder="Search products" />
          {isLoading && <Text size="small" className="text-ui-fg-muted">Loading…</Text>}
          <ul className="divide-y rounded-md border">
            {data?.products.map((p: any) => {
              const inHere = p.collection?.id === collectionId
              const elsewhere = p.collection && !inHere
              return (
                <li key={p.id}>
                  <label className={"flex items-center gap-x-3 px-3 py-2 " + (inHere ? "opacity-50" : "cursor-pointer hover:bg-ui-bg-base-hover")}>
                    <Checkbox
                      disabled={inHere}
                      checked={inHere || picked.has(p.id)}
                      onCheckedChange={(c) =>
                        setPicked((s) => {
                          const n = new Set(s)
                          c ? n.add(p.id) : n.delete(p.id)
                          return n
                        })
                      }
                    />
                    <Thumb src={p.thumbnail} size={36} />
                    <div className="min-w-0">
                      <Text size="small" weight="plus" className="truncate">{p.title}</Text>
                      {elsewhere && <Text size="xsmall" className="text-ui-fg-muted">In “{p.collection.title}” — adding moves it here</Text>}
                      {inHere && <Text size="xsmall" className="text-ui-fg-muted">Already in this collection</Text>}
                    </div>
                  </label>
                </li>
              )
            })}
          </ul>
        </Drawer.Body>
        <Drawer.Footer>
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button
            size="small"
            disabled={!picked.size}
            isLoading={saving}
            onClick={async () => {
              setSaving(true)
              try {
                await onAdd([...picked])
                toast.success(`Added ${picked.size} product${picked.size === 1 ? "" : "s"}`)
                onClose()
              } catch (err) {
                toast.error(errorMessage(err))
              } finally {
                setSaving(false)
              }
            }}
          >
            Add {picked.size || ""}
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}
